import { createContext, useCallback, useMemo, useState } from 'react';
import type { FunctionComponent } from 'react';

import { SBOM_PORTAL } from '../../../../../shared/featureFlags/flagNames';
import { window } from '../../../../globals';
import { isFeatureEnabled } from '../../../../helpers/features/featureFlags';
import useAsyncRequest from '../../../../hooks/useAsyncRequest';
import { get } from '../../../../redux/api';
import { getPortalUrl } from '../../../settings/organization/portal/hooks/utils';
import type { Slug } from '../../../../../shared/settings/portal';

/**
 * Fetches the portal URL for the organization. Return undefined if the slug is not set.
 * This hook do not need any permissions as it is used to display the URL for the public portal.
 */
const usePortalUrl = (organizationId: number) => {
  const getEndpoint = useCallback(
    // User often needs to go to the portal settings page to set the slug.
    // The no-cache option ensures the user always get the latest slug for the portal URL.
    () =>
      get<Slug>(
        `/api/organizations/${organizationId}/settings/portal/slug`,
        true,
        {},
        {
          cache: 'no-cache',
        },
      ),
    [organizationId],
  );

  const { value, errors, isLoading } = useAsyncRequest(getEndpoint);

  return {
    url: value?.slug ? getPortalUrl(value.slug) : null,
    errors,
    isLoading,
  };
};

type ContextValues = {
  url: string | null;
  setSlug: (url: string | null) => void;
};

export const Context = createContext<ContextValues>({
  url: null,
  setSlug: () => {},
});

/**
 * Provider for the portal URL context. This provider fetches the portal URL for the organization and provides a way to override the URL.
 */
const PortalUrlProvider: FunctionComponent = ({ children }) => {
  const organizationId = window.user?.organizationId as number;
  const { url } = usePortalUrl(organizationId);
  const [slug, setSlug] = useState<string | null>(null);
  const value = useMemo(
    () => ({ url: getPortalUrl(slug) || url, setSlug }),
    [slug, url],
  );

  return <Context.Provider value={value}>{children}</Context.Provider>;
};

/**
 * Hook to use the portal URL context. If the feature flag is not enabled, render the children without the context.
 * This ensures the page doesn't make unnecessary requests to the backend.
 */
export const Provider: FunctionComponent = ({ children }) => {
  const isSBOMPortalEnabled = isFeatureEnabled(SBOM_PORTAL);

  if (!isSBOMPortalEnabled) {
    // Ensure the return type is a react element
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <>{children}</>;
  }

  return <PortalUrlProvider>{children}</PortalUrlProvider>;
};
